// PR-D: Submission History with the archive behind each row. The point: when clearance says "this declaration is
// wrong" staff can open exactly what was SENT (the stored CSV, under its original filename) and what Descartes
// ANSWERED (the stored response, verbatim) -- never a regenerated file, which would already reflect later edits.
import { useEffect, useMemo, useState } from 'react'
import type { EcustomsSubmission, SubmissionArchive } from '../../hooks/useECustoms'
import { orderedFields, responseSummary, filenameFromRequestXml } from '../../lib/descartesResponse'

interface Props {
  jobRef: string
  submissions: EcustomsSubmission[]
  getArchive: (submissionId: string) => Promise<SubmissionArchive>
  statusLabel: (status: string) => string
}

type Filter = 'all' | 'sent' | 'generated'
const isSent = (s: EcustomsSubmission) => s.status !== 'generated'

function download(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function Tag({ children, tone = 'gray' }: { children: React.ReactNode; tone?: 'gray' | 'blue' | 'amber' }) {
  const c = tone === 'blue' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
    : tone === 'amber' ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  return <span className={`mr-1 inline-block rounded px-1.5 py-0.5 text-xs font-medium ${c}`}>{children}</span>
}

export default function SubmissionHistory({ jobRef, submissions, getArchive, statusLabel }: Props) {
  const [filter, setFilter] = useState<Filter>('all')
  const [open, setOpen] = useState<string | null>(null)
  const [archives, setArchives] = useState<Record<string, SubmissionArchive | 'loading' | { error: string }>>({})
  const [raw, setRaw] = useState<{ title: string; text: string } | null>(null)

  const sentCount = submissions.filter(isSent).length
  const rows = useMemo(() => submissions.filter(s => filter === 'all' || (filter === 'sent' ? isSent(s) : !isSent(s))), [submissions, filter])

  const load = (id: string) => {
    if (archives[id]) return
    setArchives(a => ({ ...a, [id]: 'loading' }))
    getArchive(id).then(r => setArchives(a => ({ ...a, [id]: r }))).catch(e => setArchives(a => ({ ...a, [id]: { error: e instanceof Error ? e.message : String(e) } })))
  }
  // Rows that were actually sent: fetch their stored response up front so the Descartes reference/key shows in the table.
  useEffect(() => { submissions.filter(isSent).forEach(s => load(s.id)) /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [submissions])

  const arch = (id: string) => { const a = archives[id]; return a && a !== 'loading' && !('error' in a) ? a : null }
  const fileName = (s: EcustomsSubmission) => s.file_name || filenameFromRequestXml(arch(s.id)?.request_xml) || null

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Submission History</h2>
        <div className="flex gap-1 text-xs">
          {([['all', `All (${submissions.length})`], ['sent', `Sent to Descartes (${sentCount})`], ['generated', `Generated only (${submissions.length - sentCount})`]] as [Filter, string][]).map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} className={`rounded-full border px-3 py-1 ${filter === k ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300'}`}>{label}</button>
          ))}
        </div>
      </div>
      {rows.length === 0 && <p className="px-6 py-4 text-sm text-gray-500">Nothing in this filter.</p>}
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {rows.map(s => {
          const sent = isSent(s)
          const a = arch(s.id)
          const sum = sent && a ? responseSummary(a.response_xml) : null
          const isOpen = open === s.id
          const by = sent ? (s.submitted_by_email || s.generated_by_email) : s.generated_by_email
          return (
            <div key={s.id} className={sent ? 'border-l-4 border-blue-500' : 'border-l-4 border-transparent'}>
              <button className="grid w-full grid-cols-12 items-center gap-2 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40"
                onClick={() => { setOpen(isOpen ? null : s.id); if (!isOpen) load(s.id) }}>
                <span className="col-span-2 font-mono text-xs text-gray-600 dark:text-gray-300">{new Date(s.submitted_at || s.created_at).toLocaleString()}</span>
                <span className="col-span-2 truncate text-xs text-gray-600 dark:text-gray-300" title={by ?? ''}>{by ?? '—'}</span>
                <span className="col-span-2 truncate font-mono text-xs" title={fileName(s) ?? ''}>{fileName(s) ?? '—'}</span>
                <span className="col-span-2">
                  <Tag tone={s.environment === 'prd1' ? 'amber' : 'gray'}>{s.environment.toUpperCase()}</Tag>
                  <Tag tone={sent ? 'blue' : 'gray'}>{sent ? 'Submitted' : 'Generated'}</Tag>
                  {s.pva_from_csv && <Tag>{s.pva_used ? 'PVA' : 'Non-PVA'}</Tag>}
                  {s.dan_used != null && <Tag>{s.dan_used ? 'DAN' : 'No DAN'}</Tag>}
                </span>
                <span className="col-span-2 text-xs">{sent ? `${statusLabel(s.status)}${s.error_msg ? ` — ${s.error_msg}` : ''}` : <span className="text-gray-400">not sent</span>}</span>
                <span className="col-span-2 truncate font-mono text-xs text-gray-700 dark:text-gray-300">
                  {sum && sum.ids.length > 0 ? sum.ids.map(f => `${f.tag}: ${f.value}`).join(' · ') : s.reference ? `Reference: ${s.reference}` : sent && a && !a.response_xml ? <span className="text-amber-700">no response stored</span> : ''}
                </span>
              </button>
              {isOpen && (
                <div className="space-y-3 bg-gray-50 px-6 py-4 text-sm dark:bg-gray-900/30">
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Parameters this file was built with</p>
                    {s.decl_type || s.load_type || s.dan_used != null ? (
                      <p className="text-gray-800 dark:text-gray-200">
                        <Tag>{s.decl_type === 'IMA' ? 'IMA goods arrived' : s.decl_type === 'IMD' ? 'IMD goods not yet arrived' : 'Declaration type —'}</Tag>
                        <Tag>Representation {s.declarant_rep ?? '—'}</Tag>
                        <Tag>{s.pva_from_csv ? (s.pva_used ? 'PVA' : 'Non-PVA') : 'PVA —'}</Tag>
                        <Tag>{s.dan_used == null ? 'DAN —' : s.dan_used ? 'Via CY UK DAN' : 'No DAN'}</Tag>
                        <Tag>{s.load_type ? `Load ${s.load_type}` : 'Load —'}</Tag>
                        {s.vat_adjustment_amount != null && <Tag tone={s.vat_adjustment_manual ? 'amber' : 'gray'}>VAT adjustment {Number(s.vat_adjustment_amount).toFixed(2)} GBP{s.vat_adjustment_manual ? ' (edited by hand)' : ''}</Tag>}
                      </p>
                    ) : <p className="text-gray-500">Recorded before the checklist existed -- the parameters are not on file for this row.</p>}
                  </div>
                  {(() => {
                    const st = archives[s.id]
                    if (st === 'loading' || !st) return <p className="text-gray-500">Loading the stored file…</p>
                    if ('error' in st) return <p className="text-red-700">Could not load the stored file: {st.error}</p>
                    const fname = fileName(s) ?? `${jobRef}-${s.id.slice(0, 8)}.csv`
                    return (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          <button disabled={!st.request_csv} onClick={() => download(fname, st.request_csv!)} className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">Download the CSV as {sent ? 'sent' : 'generated'} ({fname})</button>
                          <button disabled={!st.response_xml} onClick={() => setRaw({ title: 'Descartes response (verbatim)', text: st.response_xml! })} className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs disabled:opacity-40 dark:border-gray-600">View Descartes response</button>
                          <button disabled={!st.request_xml} onClick={() => setRaw({ title: 'Request envelope (credentials masked)', text: st.request_xml! })} className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs disabled:opacity-40 dark:border-gray-600">View request XML</button>
                        </div>
                        {!st.request_csv && <p className="text-amber-800">No CSV was stored for this row.</p>}
                        {sent && !st.response_xml && <p className="text-amber-800">No response was recorded for this submission. The connection may have dropped after sending -- check in Descartes before sending again.</p>}
                        {st.response_xml && (
                          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5 text-xs">
                            {orderedFields(st.response_xml).map((f, i) => (<div key={i} className="contents"><dt className="text-gray-500">{f.tag}</dt><dd className="font-mono">{f.value}</dd></div>))}
                          </dl>
                        )}
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {raw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl dark:bg-gray-800">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">{raw.title}</h3>
              <div className="flex gap-3 text-sm">
                <button className="text-blue-600 underline" onClick={() => navigator.clipboard?.writeText(raw.text)}>Copy</button>
                <button className="text-gray-600 dark:text-gray-300" onClick={() => setRaw(null)}>Close</button>
              </div>
            </div>
            <pre className="overflow-auto whitespace-pre-wrap break-all p-5 font-mono text-xs text-gray-800 dark:text-gray-200">{raw.text}</pre>
          </div>
        </div>
      )}
    </div>
  )
}
